import type { WorkAssignment } from '@/components/IAP/hooks/workAssignmentHooks';
import type { WorkAssignmentListAttachmentData } from '@/components/IAP/hooks/assignmentListHooks';
import type { AssignmentListItem } from './columns';
import dayjs from 'dayjs';
import { mapProposedStatus } from '@/components/IAP/planningView/widgets/AssignmentList/AssignmentList';

const createWorkAssignmentItem = (wa: WorkAssignment) => ({
  id: wa.id,
  type: '',
  name: wa.work_assignment ?? '',
  description: '',
  workGroupName: wa.work_group_name ?? '',
  workGroupId: wa.work_group_id!,
  overheadPosition: wa.overhead_positions ?? '',
  specialInstructions: wa.special_instructions ?? '',
  reportingLocation: wa.reporting_location ?? '',
  timestamp: wa.timestamp ? dayjs(wa.timestamp).toISOString() : '',
});

const createResourceNeeds = (wa: WorkAssignment) =>
  wa.resource_list?.map((resource) => ({
    ...resource,
    workAssignmentId: wa.id,
    workAssignmentName: wa.work_assignment ?? '',
  })) ?? [];

export const createAssignmentListTableData = ({
  workAssignments,
  workAssignmentListAttachments,
  periodNumber,
}: {
  workAssignments?: WorkAssignment[];
  workAssignmentListAttachments?: WorkAssignmentListAttachmentData[];
  periodNumber: number;
}) => {
  if (!workAssignmentListAttachments?.length) return [];

  const workAssignmentsByGroupId = new Map<string, WorkAssignment[]>();

  workAssignments
    ?.filter((wa) => wa.operational_period_number === periodNumber)
    .forEach((wa) => {
      if (wa.work_group_id) {
        const groupAssignments =
          workAssignmentsByGroupId.get(wa.work_group_id) || [];
        groupAssignments.push(wa);
        workAssignmentsByGroupId.set(wa.work_group_id, groupAssignments);
      }
    });

  return workAssignmentListAttachments
    .filter((attachment) => attachment.operationalPeriodNumber === periodNumber)
    .map((attachment) => {
      const workAssignmentsForGroup =
        workAssignmentsByGroupId.get(attachment.workGroupId) || [];

      const assignmentItems = workAssignmentsForGroup.map(
        createWorkAssignmentItem
      );
      const resourceNeeds =
        workAssignmentsForGroup.flatMap(createResourceNeeds);

      const latestWorkAssignmentTimestamp =
        workAssignmentsForGroup.reduce<string>((latest, wa) => {
          const waTimestamp = wa.timestamp
            ? dayjs(wa.timestamp).toISOString()
            : '';
          return waTimestamp &&
            (!latest || dayjs(waTimestamp).isAfter(dayjs(latest)))
            ? waTimestamp
            : latest;
        }, '');

      return {
        walaId: attachment.walaId,
        workGroupId: attachment.workGroupId,
        workGroup: attachment.workGroupName,
        branch: attachment.branch,
        division: attachment.division,
        walaGroup: attachment.walaGroup,
        stagingArea: attachment.stagingArea,
        proposedStatus: mapProposedStatus(
          attachment.proposedStatus,
          attachment.status
        ),
        actualStatus: attachment.status,
        sectionChief: attachment.sectionChief,
        branchDirector: attachment.branchDirector,
        divisionGroupSupervisor: attachment.divisionGroupSupervisor,
        workAssignments: assignmentItems,
        workGroupResourceNeeds: resourceNeeds,
        plannedResources: attachment.plannedResources,
        medicalCommunication: attachment.medicalCommunication,
        evacuationCommunication: attachment.evacuationCommunication,
        otherCommunication: attachment.otherCommunication,
        specialInstructions: attachment.specialInstructions,
        timestamp: latestWorkAssignmentTimestamp
          ? dayjs(latestWorkAssignmentTimestamp).toDate()
          : undefined,
      };
    }) as AssignmentListItem[];
};
